/**
 * File upload utility — picks a file via native dialog (Tauri) or
 * HTML file input (browser), then uploads it to the backend.
 */

import { detectEnv } from "../tauri/env";
import type { AppTransport } from "../runtime/contracts/transport";
import { authHeaders } from "../store/authStore";

export interface UploadedFile {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

interface FileUploadRpcResult {
  readonly id: string;
  readonly name: string;
  readonly mime_type: string;
  readonly size_bytes: number;
}

/** HTTP POST /files/upload returns entity_id instead of id */
interface FileUploadHttpResult {
  readonly entity_id: string;
  readonly name: string;
  readonly mime_type: string;
  readonly size_bytes: number;
}

/** Map RPC response (file.upload) to UploadedFile. */
export function mapRpcResult(result: { id: string; name: string; mime_type: string; size_bytes: number }): UploadedFile {
  return { id: result.id, name: result.name, mimeType: result.mime_type, sizeBytes: result.size_bytes };
}

/** Map HTTP response (POST /files/upload) to UploadedFile. */
export function mapHttpResult(result: { entity_id: string; name: string; mime_type: string; size_bytes: number }): UploadedFile {
  return { id: result.entity_id, name: result.name, mimeType: result.mime_type, sizeBytes: result.size_bytes };
}

/** Extract filename from an absolute path (handles both / and \ separators). */
export function extractFilename(filePath: string): string {
  const parts = filePath.split("/");
  const lastSlash = parts[parts.length - 1];
  if (lastSlash && lastSlash !== filePath) return lastSlash;
  // Windows-style backslash path
  const bparts = filePath.split("\\");
  return bparts[bparts.length - 1] ?? filePath;
}

/**
 * Opens a file picker and uploads the selected file.
 * Returns the uploaded file metadata, or null if the user cancelled.
 */
export async function uploadFile(transport: AppTransport): Promise<UploadedFile | null> {
  const env = detectEnv();

  if (env.isTauri) {
    return uploadViaTauri(transport);
  }
  return uploadViaBrowser(transport);
}

async function uploadViaTauri(transport: AppTransport): Promise<UploadedFile | null> {
  const { open } = await import("@tauri-apps/plugin-dialog");

  const selected = await open({
    multiple: false,
    directory: false,
  });

  if (!selected) return null;

  const name = extractFilename(selected);

  const result = await transport.rpc<FileUploadRpcResult>("file.upload", {
    name,
    local_path: selected,
  });

  return mapRpcResult(result);
}

async function uploadViaBrowser(transport: AppTransport): Promise<UploadedFile | null> {
  const file = await pickFileViaInput();
  if (!file) return null;
  return uploadBrowserFile(transport, file);
}

/**
 * Upload a pre-picked browser File to the backend. Useful when the caller
 * owns its own `<input type="file">` (e.g. an attachment picker inside a
 * composer).
 */
export async function uploadBrowserFile(
  transport: AppTransport,
  file: File,
): Promise<UploadedFile> {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const response = await fetch(`${transport.baseUrl}/files/upload`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  const result = (await response.json()) as FileUploadHttpResult;
  return mapHttpResult(result);
}

function pickFileViaInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.style.display = "none";

    input.addEventListener("change", () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    });

    // Handle cancel — the input won't fire "change" if cancelled,
    // but the window regains focus after the dialog closes.
    const handleFocus = (): void => {
      window.removeEventListener("focus", handleFocus);
      // Small delay to let "change" fire first if a file was selected.
      setTimeout(() => {
        if (input.parentElement) {
          input.remove();
          resolve(null);
        }
      }, 300);
    };
    window.addEventListener("focus", handleFocus);

    document.body.appendChild(input);
    input.click();
  });
}
